import { Timestamp } from "@bufbuild/protobuf";

import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";

import {
  SudoHistoryFilters,
  Timeframe,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/sudo_filters_pb";
import { SudoRecord } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import RPCTablePaginationActions from "../components/shared/table/rpc-table-pagination-actions";
import { DEFAULT_REGION_CODE, SudoRequestStatus } from "../constants/index";
import {
  convertTimestampToDate,
  convertToTimestamp,
  formatDateTimeDisplay,
} from "../helpers/time";
import { RegionCode } from "../types/region";
import { listSudoHistory } from "api/endpoints/activity-history";
import { callOptionsHeaders } from "api/helpers";
import DatePicker from "components/shared/date-picker";
import { SnackbarContext } from "context/snackbar-context";

interface SudoFilters {
  requestedBy: string;
  userId: string;
  from?: Date;
  to?: Date;
}

const SudoHistory = () => {
  const [regionCode, setRegionCode] = useState<RegionCode>(DEFAULT_REGION_CODE);
  const [sudoHistory, setSudoHistory] = useState<SudoRecord[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [paginationValues, setPaginationValues] = useState({
    pageSize: 25,
    offset: 0,
  });
  const now = DateTime.now();
  const defaultFrom = now.startOf("day").minus({ days: 30 });
  const [isLoading, setIsLoading] = useState(false);
  const [sudoHistoryFilters, setSudoHistoryFilters] = useState<SudoFilters>({
    requestedBy: "",
    userId: "",
    from: defaultFrom.toJSDate(),
    to: now.endOf("day").toJSDate(),
  });

  const { showError } = useContext(SnackbarContext);

  const updateSudoHistoryFilters = (
    key: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSudoHistoryFilters({
      ...sudoHistoryFilters,
      [key]: event.target.value,
    });
  };

  const handleRegionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRegionCode(event.target.value as RegionCode);
  };

  const getSudoHistory = (
    pageSize: number,
    offSet: number,
    sudoHistoryFilters: SudoFilters
  ) => {
    setIsLoading(true);
    const filters = new SudoHistoryFilters();
    if (sudoHistoryFilters.userId !== "") {
      filters.userId = sudoHistoryFilters.userId;
    }
    if (sudoHistoryFilters.requestedBy !== "") {
      filters.requestedBy = sudoHistoryFilters.requestedBy;
    }
    if (
      sudoHistoryFilters.from !== undefined ||
      sudoHistoryFilters.to !== undefined
    ) {
      const timeframe = new Timeframe();
      if (sudoHistoryFilters.from !== undefined) {
        const formattedFrom = convertToTimestamp(sudoHistoryFilters.from);
        timeframe.from = formattedFrom;
      }
      if (sudoHistoryFilters.to !== undefined) {
        const formattedTo = convertToTimestamp(sudoHistoryFilters.to);
        timeframe.to = formattedTo;
      }
      filters.requestedTimeframe = timeframe;
    }

    listSudoHistory(
      { pageSize, offset: offSet, filters },
      callOptionsHeaders(regionCode)
    )
      .then((sudoHist) => {
        setSudoHistory(sudoHist.sudoRequests);
        setTotalResults(sudoHist.totalItems);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getSudoHistory(
      paginationValues.pageSize,
      paginationValues.offset,
      sudoHistoryFilters
    );
    // eslint-disable-next-line
  }, []);

  const handleResultsPageUpdate = (newOffset: number) => {
    setPaginationValues({
      pageSize: paginationValues.pageSize,
      offset: newOffset,
    });
    getSudoHistory(paginationValues.pageSize, newOffset, sudoHistoryFilters);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    const newOffset = newPage * paginationValues.pageSize;
    handleResultsPageUpdate(newOffset);
  };

  const clearSearch = () => {
    setSudoHistoryFilters({
      requestedBy: "",
      userId: "",
      from: defaultFrom.toJSDate(),
      to: DateTime.now().endOf("day").toJSDate(),
    });
  };

  const handleUpdateDate = (key: string, updatedDate: Date) => {
    if (key === "from") {
      setSudoHistoryFilters({
        ...sudoHistoryFilters,
        from: updatedDate,
      });
    }
    if (key === "to") {
      setSudoHistoryFilters({
        ...sudoHistoryFilters,
        to: updatedDate,
      });
    }
  };

  const determineUnusedSudoStatus = (timeStamp: Timestamp) => {
    const createdAtDate = convertTimestampToDate(timeStamp);
    const currentDate = new Date(Date.now());
    const updatedCreatedAtDate = new Date(createdAtDate.getTime() + 15 * 60000);

    if (updatedCreatedAtDate < currentDate) {
      return SudoRequestStatus.EXPIRED;
    }

    return SudoRequestStatus.PENDING;
  };

  const sudoRequestRecord = (row: SudoRecord, index: number) => {
    let sudoStatus = SudoRequestStatus.USED;

    if (row.consumedAt === undefined && row.createdAt) {
      sudoStatus = determineUnusedSudoStatus(row.createdAt);
    }

    return (
      <TableRow key={`${index}-${row.userId}`}>
        <TableCell align="left" style={{ verticalAlign: "top" }}>
          {row.requestedBy}
          <br />
          <Typography variant="caption" gutterBottom>
            {row?.createdAt &&
              formatDateTimeDisplay(convertTimestampToDate(row?.createdAt))}
          </Typography>
        </TableCell>
        <TableCell align="left" style={{ verticalAlign: "top" }}>
          {row.userId}
        </TableCell>
        <TableCell
          align="left"
          style={{
            verticalAlign: "top",
            whiteSpace: "normal",
            wordBreak: "break-all",
          }}
        >
          {row.reason}
        </TableCell>
        <TableCell align="left" style={{ verticalAlign: "top" }}>
          {sudoStatus}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <form noValidate autoComplete="off">
          <Grid container spacing={1} justifyContent="center">
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="region"
                  name="region-radio-group"
                  value={regionCode}
                  row={true}
                  onChange={handleRegionChange}
                >
                  <FormControlLabel value="us" label="US" control={<Radio />} />
                  <FormControlLabel value="au" label="AU" control={<Radio />} />
                  <FormControlLabel value="eu" label="EU" control={<Radio />} />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                id="requested-by-input"
                label="Requested by"
                value={sudoHistoryFilters.requestedBy}
                onChange={(event) =>
                  updateSudoHistoryFilters("requestedBy", event)
                }
                variant="outlined"
                size="small"
                fullWidth
                inputProps={{ "data-anchor": "requested-by-input" }}
                onKeyUp={(event) => {
                  if (event.key === "Enter") {
                    getSudoHistory(
                      paginationValues.pageSize,
                      paginationValues.offset,
                      sudoHistoryFilters
                    );
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                id="userId-input"
                label="User ID"
                value={sudoHistoryFilters.userId}
                onChange={(event) => updateSudoHistoryFilters("userId", event)}
                variant="outlined"
                size="small"
                fullWidth
                inputProps={{ "data-anchor": "user-id-input" }}
                onKeyUp={(event) => {
                  if (event.key === "Enter") {
                    getSudoHistory(
                      paginationValues.pageSize,
                      paginationValues.offset,
                      sudoHistoryFilters
                    );
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={1.5}>
              <DatePicker
                label="From"
                size="small"
                defaultDateSelection={sudoHistoryFilters.from}
                onChange={(date) => {
                  handleUpdateDate("from", date);
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={1.5}>
              <DatePicker
                label="To"
                size="small"
                defaultDateSelection={sudoHistoryFilters.to}
                onChange={(date) => {
                  handleUpdateDate("to", date);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    getSudoHistory(
                      paginationValues.pageSize,
                      paginationValues.offset,
                      sudoHistoryFilters
                    );
                  }}
                  data-anchor="load-sudo-hist-btn"
                  data-testid="load-sudo-hist-btn"
                >
                  Load
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={clearSearch}
                  data-testid="sudo-history-search-bar-clear-button"
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Grid>

      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table aria-label="sudo-history">
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  <b>Requested By</b>
                </TableCell>
                <TableCell align="left">
                  <b>User ID</b>
                </TableCell>
                <TableCell align="left">
                  <b>Reason</b>
                </TableCell>
                <TableCell align="left">
                  <b>Status</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                sudoHistory.map((row, index) => sudoRequestRecord(row, index))
              )}
            </TableBody>
            <TableFooter>
              <TablePagination
                rowsPerPageOptions={[paginationValues.pageSize]}
                count={totalResults}
                rowsPerPage={paginationValues.pageSize}
                page={Math.round(
                  paginationValues.offset / paginationValues.pageSize
                )}
                component="td"
                onPageChange={handleChangePage}
                ActionsComponent={RPCTablePaginationActions}
              />
            </TableFooter>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default SudoHistory;
